// src/components/profile/ProfileSidebar.tsx
"use client";

import React from 'react';
import Image from 'next/image';
import ProfileSnapshotFields, { type SnapshotBasics } from '@/components/profile/ProfileSnapshotFields';
import ProfileMoreInfoAccordion, { type MoreInfo } from '@/components/profile/ProfileMoreInfoAccordion';
import ProfileCTAs from '@/components/profile/ProfileCTAs';
import { toAbsoluteMediaUrl } from '@/lib/media';
import { BadgeCheck, Link as LinkIcon, Film, Star, Instagram, Twitter } from 'lucide-react';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import FriendsSidebarSection from '@/components/profile/FriendsSidebarSection';
import { scrubContactInfo } from '@/lib/scrubContact';

export type Verification = {
  idVerified?: boolean;
  urlVerified?: boolean;
  instagram?: string | null;
  twitter?: string | null;
  film?: boolean;
  featured?: boolean;
};

export default function ProfileSidebar({
  personId,
  displayName,
  avatarUrl,
  basics,
  verification,
  moreInfo,
  unsafe = false,
  showCTAs = true,
  showInfo = true,
  showBio = false,
  fallbackBio = null,
}: {
  personId: string | number;
  displayName: string;
  avatarUrl?: string | null;
  basics: SnapshotBasics;
  verification?: Verification | null;
  moreInfo?: MoreInfo | null;
  unsafe?: boolean;
  showCTAs?: boolean;
  showInfo?: boolean;
  showBio?: boolean;
  fallbackBio?: string | null;
}) {
  // avatarUrl already arrives with the correct URL for this viewer from the API
  // (blurred for guests, original for authed). Just resolve it — no client-side gating.
  const avatar = avatarUrl ? toAbsoluteMediaUrl(avatarUrl) : '/img/default-avatar.png';
  const [showSidebarAvatar, setShowSidebarAvatar] = React.useState(false);
  const [isDesktop, setIsDesktop] = React.useState(false);
  // Observe the hero banner avatar sentinel; show sidebar avatar only after it scrolls off
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    // Detect desktop viewport for forcing More Info open
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    try { update(); mq.addEventListener('change', update); } catch {
      // Safari fallback
      try { mq.addListener(update); } catch {}
    }
    const el = document.getElementById('banner-avatar-sentinel');
    if (!el) { setShowSidebarAvatar(true); return; }
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // When sentinel is visible, keep sidebar avatar hidden
        setShowSidebarAvatar(!entry.isIntersecting);
      }
    }, { rootMargin: '0px', threshold: 0 });
    obs.observe(el);
    return () => { try { obs.disconnect(); } catch {}; try { mq.removeEventListener('change', update); } catch { try { mq.removeListener(update); } catch {} } };
  }, []);

  return (
    <aside className="md:sticky md:top-[64px] space-y-4">
      {/* Sidebar avatar: appears after banner avatar scrolls off */}
      {showSidebarAvatar && (
        <div className="grid place-items-center text-center">
          <div className="relative h-28 w-28">
            <Image
              src={avatar}
              alt={`${displayName} avatar`}
              fill
              sizes="112px"
              className="rounded-full object-cover border-2 border-white dark:border-zinc-900 shadow-md"
              priority={false}
            />
          </div>
          <div className="mt-2 text-base font-semibold leading-tight inline-flex items-center justify-center gap-1.5">
            <span>{scrubContactInfo(displayName)}</span>
            {verification?.idVerified && <VerifiedBadge size={16} />}
          </div>
        </div>
      )}
      {showInfo && (
        <div className="space-y-4">
          {(() => {
            const g = (basics?.gender || '').toString().trim().toLowerCase();
            const isFemale = g === 'female' || g === 'f' || g.includes('woman');
            const pick = (a?: any, b?: any) => {
              const sa = (a != null && String(a).trim()) ? String(a).trim() : '';
              const sb = (b != null && String(b).trim()) ? String(b).trim() : '';
              return sa || sb || '';
            };
            const fmtHeight = (val?: string | number | null) => {
              if (val == null) return '';
              const n = Number(val);
              if (!Number.isFinite(n) || n <= 0) return String(val);
              const ft = Math.floor(n / 12); const inch = Math.round(n % 12);
              return `${ft}' ${inch}\" (${n} in)`;
            };
            const fmtWeight = (val?: string | number | null) => {
              if (val == null) return '';
              const n = Number(val);
              if (!Number.isFinite(n) || n <= 0) return String(val);
              return `${n} lbs`;
            };
            const fmtYesNo = (v?: any) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : (String(v || '').trim()));

            const rows: Array<{ label: string; value: string }> = [];
            if (basics?.age != null && String(basics.age).trim()) rows.push({ label: 'Age', value: String(basics.age) });
            if ((basics?.ethnicity && String(basics.ethnicity).trim())) rows.push({ label: 'Ethnicity', value: String(basics.ethnicity) });
            if ((basics?.gender && String(basics.gender).trim())) rows.push({ label: 'Gender', value: String(basics.gender) });
            if ((basics?.location && String(basics.location).trim())) rows.push({ label: 'Location', value: String(basics.location) });
            const hVal = pick((moreInfo as any)?.height, (basics as any)?.heightIn);
            const wVal = pick((moreInfo as any)?.weight, (basics as any)?.weightLb);
            if (hVal) rows.push({ label: 'Height', value: fmtHeight(hVal) });
            if (wVal) rows.push({ label: 'Weight', value: fmtWeight(wVal) });
            if ((moreInfo as any)?.relocate != null && String((moreInfo as any)?.relocate).trim()) rows.push({ label: 'Relocate', value: fmtYesNo((moreInfo as any)?.relocate) });
            const dickSize = (moreInfo as any)?.dickSize;
            const boobsSize = (moreInfo as any)?.boobsSize;
            if (!isFemale && dickSize != null && String(dickSize).trim() && String(dickSize).trim() !== '-') {
              const n = Number(dickSize); rows.push({ label: 'Dick Size', value: (Number.isFinite(n) && n > 0) ? `${n} in` : String(dickSize) });
            }
            if (isFemale && boobsSize != null && String(boobsSize).trim() && String(boobsSize).trim() !== '-') rows.push({ label: 'Boobs Size', value: String(boobsSize) });
            if ((moreInfo as any)?.hair && String((moreInfo as any)?.hair).trim()) rows.push({ label: 'Hair', value: String((moreInfo as any)?.hair) });
            if ((moreInfo as any)?.eyes && String((moreInfo as any)?.eyes).trim()) rows.push({ label: 'Eyes', value: String((moreInfo as any)?.eyes) });
            if ((moreInfo as any)?.alterations && String((moreInfo as any)?.alterations).trim()) {
              // Raw enum values like "light_tats" leak from the API; humanize.
              const alt = String((moreInfo as any).alterations).trim().replace(/_/g, ' ');
              rows.push({ label: 'Alterations', value: alt.charAt(0).toUpperCase() + alt.slice(1) });
            }
            if ((moreInfo as any)?.bodyType && String((moreInfo as any)?.bodyType).trim()) {
              const btMap: Record<string, string> = { '1': 'Very Skinny', '2': 'Skinny', '3': 'Average', '4': 'Muscular', '5': 'Overweight' };
              const btRaw = String((moreInfo as any).bodyType).trim();
              rows.push({ label: 'Body Type', value: btMap[btRaw] || btRaw });
            }
            if ((moreInfo as any)?.chest && String((moreInfo as any)?.chest).trim() && String((moreInfo as any)?.chest).trim() !== '-') rows.push({ label: 'Chest', value: String((moreInfo as any)?.chest) });

            const bio = (moreInfo?.bio && String(moreInfo.bio).trim()) ? String(moreInfo.bio) : (fallbackBio && String(fallbackBio).trim()) ? String(fallbackBio) : '';

            // Group stats into sections
            const physicalStats = ['Age', 'Height', 'Weight', 'Body Type', 'Boobs Size', 'Dick Size', 'Chest'];
            const personalStats = ['Ethnicity', 'Gender', 'Location', 'Hair', 'Eyes', 'Alterations'];
            const otherStats = ['Relocate'];

            const physical = rows.filter(r => physicalStats.includes(r.label));
            const personal = rows.filter(r => personalStats.includes(r.label));
            const other = rows.filter(r => otherStats.includes(r.label));

            const [bioExpanded, setBioExpanded] = React.useState(false);
            const bioLines = bio ? bio.split('\n') : [];
            const shouldCollapseBio = bioLines.length > 3 || bio.length > 200;
            const displayBio = (shouldCollapseBio && !bioExpanded)
              ? bioLines.slice(0, 3).join('\n') + (bioLines.length > 3 ? '...' : '')
              : bio;

            // h3, not h4: the page outline is h1 (name) > h2 (sr-only region
            // headings in page.tsx) > h3 (these groups + card titles).
            const renderSection = (label: string, items: typeof physical) => items.length > 0 && (
              <div>
                <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                  {label}
                </h3>
                <dl className="text-sm">
                  {items.map((r, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between gap-3 border-t border-zinc-200/70 py-2 first:border-t-0 dark:border-zinc-800"
                    >
                      <dt className="whitespace-nowrap text-zinc-500 dark:text-zinc-400">{r.label}</dt>
                      <dd className="min-w-0 break-words text-right font-medium text-zinc-900 dark:text-zinc-100">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );

            // Sort + normalize display case: the raw list mixes "Acting",
            // "hardcore", "production" in insertion order.
            const skills = (Array.isArray((moreInfo as any)?.skills)
              ? ((moreInfo as any).skills as unknown[]).map((s) => String(s).trim()).filter(Boolean)
              : [])
              .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

            const hasInfo = physical.length > 0 || personal.length > 0 || other.length > 0 || skills.length > 0;
            return (
              <div className="space-y-4">
                {bio && (
                  <div className="rounded-2xl border border-border bg-card text-card-foreground shadow-sm p-5">
                    <p className="font-serif text-base italic leading-relaxed text-zinc-700 dark:text-zinc-200">
                      &ldquo;{scrubContactInfo(displayBio)}&rdquo;
                    </p>
                    {shouldCollapseBio && (
                      <button
                        type="button"
                        onClick={() => setBioExpanded(!bioExpanded)}
                        className="mt-2 text-xs text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        {bioExpanded ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                )}

                {hasInfo && (
                  <div className="rounded-2xl border border-border bg-card text-card-foreground shadow-sm p-5 space-y-5">
                    {renderSection('Physical', physical)}
                    {renderSection('Personal', personal)}
                    {renderSection('Other', other)}
                    {skills.length > 0 && (
                      <div>
                        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                          Skills
                        </h3>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.map((s, i) => (
                            <span
                              key={i}
                              className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-zinc-700 dark:text-zinc-200"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showCTAs && (
                  <ProfileCTAs personId={personId} variant="brand" size="lg" layout="inline" className="justify-center" />
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Standalone Bio block (when showInfo is false but we still want Bio) */}
      {!showInfo && showBio && (
        (() => { const bio = (moreInfo?.bio && String(moreInfo.bio).trim()) ? String(moreInfo.bio) : (fallbackBio && String(fallbackBio).trim()) ? String(fallbackBio) : null; return bio ? (
        <div className="rounded-xl border border-border bg-card text-card-foreground overflow-hidden shadow-sm p-3">
          <h3 className="text-base font-semibold mb-2">Bio</h3>
          <p className="text-base md:text-lg leading-relaxed whitespace-pre-line">{scrubContactInfo(bio)}</p>
        </div>
        ) : null; })()
      )}
      {/* Friends Section with better card treatment */}
      <div className="mt-4">
        <FriendsSidebarSection personId={personId} />
      </div>
    </aside>
  );
}
